import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { UserProfile } from './types';
import { Navbar } from './components/UI';
import { Footer } from './components/Footer';
import { initGA, GAPageTracker } from './components/GoogleAnalytics';
import { Home } from './pages/Home';

// Lazy load secondary pages and modal for better initial bundle performance
const SurpriseMeModal = lazy(() => import('./components/SurpriseMeModal').then(m => ({ default: m.SurpriseMeModal })));
const Watch = lazy(() => import('./pages/Watch').then(m => ({ default: m.Watch })));
const History = lazy(() => import('./pages/History').then(m => ({ default: m.History })));
const Watchlist = lazy(() => import('./pages/Watchlist').then(m => ({ default: m.Watchlist })));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy').then(m => ({ default: m.PrivacyPolicy })));
const TermsOfService = lazy(() => import('./pages/TermsOfService').then(m => ({ default: m.TermsOfService })));
const DMCA = lazy(() => import('./pages/DMCA').then(m => ({ default: m.DMCA })));
const About = lazy(() => import('./pages/About').then(m => ({ default: m.About })));
const Contact = lazy(() => import('./pages/Contact').then(m => ({ default: m.Contact })));
const Donate = lazy(() => import('./pages/Donate').then(m => ({ default: m.Donate })));
const NotFound = lazy(() => import('./pages/NotFound').then(m => ({ default: m.NotFound })));

const AppContent = ({ user }: { user: UserProfile | null }) => {
  const [isSurpriseOpen, setIsSurpriseOpen] = useState(false);

  useEffect(() => {
    const handleOpenSurprise = () => setIsSurpriseOpen(true);
    window.addEventListener('openSurpriseMe', handleOpenSurprise);
    return () => window.removeEventListener('openSurpriseMe', handleOpenSurprise);
  }, []);

  return (
    <div className="bg-transparent min-h-screen text-white font-sans selection:bg-red-600 selection:text-white flex flex-col">
      <GAPageTracker />
      <Navbar user={user} />
      <main className="flex-grow">
        <Suspense fallback={<div className="min-h-screen bg-transparent flex items-center justify-center"><div className="w-8 h-8 rounded-full border-4 border-zinc-800 border-t-red-600 animate-spin" /></div>}>
          <Routes>
            <Route path="/" element={<Home user={user} />} />
            <Route path="/movies" element={<Home user={user} />} />
            <Route path="/tv" element={<Home user={user} />} />
            <Route path="/search" element={<Home user={user} />} />
            <Route path="/watchlist" element={<Watchlist user={user} />} />
            <Route path="/watch/:id" element={<Watch user={user} />} />
            <Route path="/:slug" element={<Watch user={user} />} />
            <Route path="/history" element={<History user={user} />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/dmca" element={<DMCA />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/donate" element={<Donate />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      {isSurpriseOpen && (
        <Suspense fallback={null}>
          <SurpriseMeModal isOpen={isSurpriseOpen} onClose={() => setIsSurpriseOpen(false)} />
        </Suspense>
      )}
      <Footer />
    </div>
  );
};

export default function App() {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  // Init Google Analytics
  useEffect(() => {
    initGA();
  }, []);

  // Disable DevTools and Right Click
  useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      // Prevent F12
      if (e.key === 'F12') {
        e.preventDefault();
      }
      // Prevent Ctrl+Shift+I / Cmd+Option+I (Inspect)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'i') {
        e.preventDefault();
      }
      // Prevent Ctrl+Shift+J / Cmd+Option+J (Console)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'j') {
        e.preventDefault();
      }
      // Prevent Ctrl+Shift+C / Cmd+Option+C (Inspect Element)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'c') {
        e.preventDefault();
      }
      // Prevent Ctrl+U / Cmd+Option+U (View Source)
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'u') {
        e.preventDefault();
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    let unsub: (() => void) | undefined;
    let cancelled = false;

    const initAuth = async () => {
      try {
        const [{ onAuthStateChanged }, { doc, getDoc }, { auth, db }] = await Promise.all([
          import('firebase/auth'),
          import('firebase/firestore'),
          import('./firebase'),
        ]);
        if (cancelled) return;

        unsub = onAuthStateChanged(auth, async (firebaseUser) => {
          if (firebaseUser) {
            try {
              const userRef = doc(db, 'users', firebaseUser.uid);
              const userSnap = await getDoc(userRef);
              if (userSnap.exists()) {
                setUser(userSnap.data() as UserProfile);
              } else {
                setUser({
                  uid: firebaseUser.uid,
                  email: firebaseUser.email || '',
                  displayName: firebaseUser.displayName || '',
                  photoURL: firebaseUser.photoURL || '',
                  role: 'user'
                });
              }
            } catch (error) {
              console.error("Error fetching user data:", error);
              setUser({
                uid: firebaseUser.uid,
                email: firebaseUser.email || '',
                displayName: firebaseUser.displayName || '',
                photoURL: firebaseUser.photoURL || '',
                role: 'user'
              });
            } finally {
              setLoading(false);
            }
          } else {
            setUser(null);
            setLoading(false);
          }
        });
      } catch (err) {
        console.error("Error initializing auth:", err);
        setLoading(false);
      }
    };

    const timer = setTimeout(initAuth, 350);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (unsub) unsub();
    };
  }, []);

  return (
    <HelmetProvider>
      <Router>
        <AppContent user={user} />
      </Router>
      <Analytics />
      <SpeedInsights />
    </HelmetProvider>
  );
}
